<template>
  <v-dialog v-model="value" max-width="700px" persistent>
    <v-card :loading="loading" :disabled="loading">
      <v-card-title class="justify-center">
        <span class="text-h5 sp">{{ $t('students.create_independent') }}</span>
      </v-card-title>

      <v-card-text>
        <v-container v-if="!credentials">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.firstName" :label="$t('students.firstname')"
                              :rules="[rules.required]" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.lastName" :label="$t('students.lastname_single')"
                              :rules="[rules.required]" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.email" :label="$t('students.email_optional')"
                              :rules="[rules.emailOptional]" />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete v-model="form.countryId" :items="countries" item-text="name"
                                item-value="id" :label="$t('students.country')"
                                :rules="[rules.required]" @change="onCountryChange" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-select v-model="form.grade" :items="gradeItems" clearable
                          :label="$t('students.grade_optional')"
                          :hint="$t('students.grade_optional_hint')" persistent-hint />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete v-model="form.timeZone" :items="timeZones" clearable
                                :label="$t('students.time_zone_optional')" />
              </v-col>
            </v-row>
          </v-form>
        </v-container>

        <v-container v-else class="text-center">
          <v-icon size="48" color="green">mdi-check-circle</v-icon>
          <h3 class="mt-2">{{ $t('students.created_title') }}</h3>
          <p class="mt-2 font-weight-bold">
            {{ $t('students.created_credentials', {
              username: credentials.username, password: credentials.password,
            }) }}
          </p>
          <v-alert type="warning" dense outlined class="text-left">
            {{ $t('students.independent_copy_warning') }}
          </v-alert>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <template v-if="!credentials">
          <v-btn color="grey" text @click="close">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="deep-purple" text :disabled="!valid || loading" @click="save">
            {{ $t('common.save') }}
          </v-btn>
        </template>
        <template v-else>
          <v-btn color="deep-purple" text @click="resetForAnother">
            {{ $t('students.create_another') }}
          </v-btn>
          <v-btn color="grey" text @click="close">{{ $t('common.close') }}</v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import countryService from '../../../services/country.service';
import studentService from '../../../services/student.service';
import snackbarMixin from '../../../mixins/snackbar';
import { COUNTRIES_CUSTOMER_ID } from '../../../constants/data';
import {
  STUDENT_GRADES,
  buildTimeZoneOptions,
  defaultTimeZoneForCountry,
  createIndependentErrorKey,
} from '../../../utils/independentStudents';

const emptyForm = () => ({
  firstName: '',
  lastName: '',
  email: '',
  countryId: null,
  grade: null,
  timeZone: null,
});

export default {
  name: 'CreateIndependentStudentModal',
  mixins: [snackbarMixin],
  props: {
    value: { type: Boolean, default: false },
  },
  data: () => ({
    loading: false,
    valid: false,
    countries: [],
    timeZones: buildTimeZoneOptions(),
    form: emptyForm(),
    // Solo en el estado local del modal: nunca en Vuex (vuex-persist lo guardaría).
    credentials: null,
    rules: {
      required: (v) => (v !== null && v !== undefined && String(v).trim() !== '') || 'Requerido.',
      emailOptional: (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Correo inválido.',
    },
  }),
  computed: {
    gradeItems() {
      return STUDENT_GRADES.map((grade) => ({
        value: grade,
        text: grade === 0
          ? this.$t('students.grade_option_0')
          : this.$t('students.grade_option_n', { grade }),
      }));
    },
  },
  async mounted() {
    try {
      const { data } = await countryService.findAll();
      this.countries = (data || []).filter((c) => COUNTRIES_CUSTOMER_ID.includes(c.id));
    } catch (e) {
      this.countries = [];
    }
  },
  methods: {
    onCountryChange() {
      this.form.timeZone = defaultTimeZoneForCountry(this.form.countryId);
    },
    async save() {
      if (this.loading || !this.$refs.form.validate()) return;
      this.loading = true;
      try {
        const result = await studentService.createIndependent({
          firstName: this.form.firstName.trim(),
          lastName: this.form.lastName.trim(),
          email: this.form.email ? this.form.email.trim() : undefined,
          grade: this.form.grade ?? null,
          countryId: this.form.countryId,
          timeZone: this.form.timeZone || undefined,
        });
        this.credentials = { username: result.username, password: result.password };
        this.$emit('onSave');
      } catch (error) {
        this.snackbarError(this.$t(createIndependentErrorKey(error)));
      } finally {
        this.loading = false;
      }
    },
    clear() {
      this.form = emptyForm();
      this.credentials = null;
      this.valid = false;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    resetForAnother() {
      this.clear();
    },
    close() {
      this.clear();
      this.$emit('onCancel');
    },
  },
};
</script>

<style scoped>
span.sp {
  font-family: "Space Grotesk" !important;
}
</style>
